import React, { ElementType } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  SimpleGrid,
  Skeleton,
  SkeletonText,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { LayoutGrid, Rows3, Search } from 'lucide-react';

export const orderAccent = '#1B7A5C';
export const orderAccentDark = '#145A44';
export const orderBorder = 'gray.200';
export const orderSurface = 'white';
export const orderSurfaceMuted = 'gray.50';

export const formatOrderMoney = (amount?: number | string | null) => {
  const numericAmount =
    typeof amount === 'string' ? Number.parseFloat(amount) : amount ?? 0;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isNaN(Number(numericAmount)) ? 0 : Number(numericAmount));
};

export const formatOrderDate = (value?: string | null) => {
  if (!value) {
    return 'Not set';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Not set';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const getOrderStatusMeta = (status?: string) => {
  switch (status) {
    case 'pending':
      return { label: 'Pending', colorScheme: 'yellow' as const };
    case 'confirmed':
      return { label: 'Confirmed', colorScheme: 'blue' as const };
    case 'processing':
      return { label: 'Processing', colorScheme: 'purple' as const };
    case 'shipped':
      return { label: 'Shipped', colorScheme: 'cyan' as const };
    case 'completed':
      return { label: 'Completed', colorScheme: 'green' as const };
    case 'cancelled':
      return { label: 'Cancelled', colorScheme: 'red' as const };
    case 'returned':
      return { label: 'Returned', colorScheme: 'orange' as const };
    case 'draft':
    default:
      return { label: 'Draft', colorScheme: 'gray' as const };
  }
};

export const getOrderPaymentMeta = (status?: string) => {
  switch (status) {
    case 'paid':
      return { label: 'Paid', colorScheme: 'green' as const };
    case 'partially_paid':
      return { label: 'Part-paid', colorScheme: 'yellow' as const };
    case 'refunded':
      return { label: 'Refunded', colorScheme: 'purple' as const };
    case 'unpaid':
    default:
      return { label: 'Unpaid', colorScheme: 'gray' as const };
  }
};

export const getOrderFulfillmentMeta = (status?: string) => {
  switch (status) {
    case 'packed':
      return { label: 'Packed', colorScheme: 'blue' as const };
    case 'out_for_delivery':
      return { label: 'On the way', colorScheme: 'purple' as const };
    case 'delivered':
      return { label: 'Delivered', colorScheme: 'green' as const };
    case 'returned':
      return { label: 'Returned', colorScheme: 'orange' as const };
    case 'unfulfilled':
    default:
      return { label: 'Not started', colorScheme: 'gray' as const };
  }
};

export const OrderShell = ({
  children,
  compact = false,
}: {
  children: React.ReactNode;
  compact?: boolean;
}) => (
  <Box
    px={{ base: 4, md: 6, xl: 8 }}
    py={{ base: 4, md: 6 }}
    bg="linear-gradient(180deg, #f4fbf8 0%, #ffffff 32%)"
    minH="calc(100vh - 72px)"
  >
    <Box maxW={compact ? '1120px' : '1360px'} mx="auto">
      {children}
    </Box>
  </Box>
);

export const OrderHero = ({
  eyebrow = 'Orders',
  title,
  description,
  primaryAction,
  secondaryAction,
  stats,
}: {
  eyebrow?: string;
  title: string;
  description: string;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  stats?: Array<{ label: string; value: string }>;
}) => (
  <Box
    bg={orderSurface}
    borderWidth="1px"
    borderColor={orderBorder}
    borderRadius={{ base: '24px', md: '28px' }}
    px={{ base: 5, md: 7 }}
    py={{ base: 5, md: 7 }}
    boxShadow="0 18px 48px rgba(15, 23, 42, 0.06)"
    mb={6}
  >
    <Flex
      align={{ base: 'stretch', lg: 'flex-start' }}
      justify="space-between"
      gap={6}
      direction={{ base: 'column', lg: 'row' }}
    >
      <VStack align="flex-start" spacing={3} maxW="3xl">
        <Text
          fontSize="xs"
          textTransform="uppercase"
          letterSpacing="0.18em"
          fontWeight="700"
          color={orderAccent}
        >
          {eyebrow}
        </Text>
        <Text
          as="h1"
          fontSize={{ base: '2xl', md: '4xl' }}
          fontWeight="800"
          lineHeight={{ base: '1.2', md: '1.05' }}
          color="gray.900"
          letterSpacing="-0.03em"
        >
          {title}
        </Text>
        <Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600" maxW="2xl">
          {description}
        </Text>
        {stats?.length ? (
          <SimpleGrid
            columns={{ base: 2, md: Math.min(stats.length, 4) }}
            spacing={3}
            w="full"
            pt={2}
          >
            {stats.map((stat) => (
              <Box
                key={stat.label}
                borderWidth="1px"
                borderColor="gray.100"
                bg={orderSurfaceMuted}
                borderRadius="18px"
                px={4}
                py={3}
              >
                <Text fontSize="xs" color="gray.500" mb={1}>
                  {stat.label}
                </Text>
                <Text fontSize="lg" fontWeight="700" color="gray.900">
                  {stat.value}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        ) : null}
      </VStack>
      {(primaryAction || secondaryAction) && (
        <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
          {primaryAction}
          {secondaryAction}
        </Stack>
      )}
    </Flex>
  </Box>
);

export const OrderPrimaryButton = ({
  children,
  ...props
}: React.ComponentProps<typeof Button>) => (
  <Button
    bg={orderAccent}
    color="white"
    _hover={{ bg: orderAccentDark, transform: 'translateY(-1px)' }}
    _active={{ bg: orderAccentDark }}
    boxShadow="0 12px 26px rgba(27, 122, 92, 0.18)"
    borderRadius="16px"
    fontWeight="700"
    px={5}
    {...props}
  >
    {children}
  </Button>
);

export const OrderSectionCard = ({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    bg={orderSurface}
    borderWidth="1px"
    borderColor={orderBorder}
    borderRadius="24px"
    px={{ base: 4, md: 6 }}
    py={{ base: 4, md: 5 }}
    boxShadow="0 12px 32px rgba(15, 23, 42, 0.04)"
  >
    <Flex
      justify="space-between"
      align={{ base: 'flex-start', md: 'center' }}
      direction={{ base: 'column', md: 'row' }}
      gap={3}
      mb={5}
    >
      <Box>
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" color="gray.900">
          {title}
        </Text>
        {description ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {description}
          </Text>
        ) : null}
      </Box>
      {action}
    </Flex>
    {children}
  </Box>
);

export const OrderMetricStrip = ({
  items,
}: {
  items: Array<{ label: string; value: string; hint?: string }>;
}) => (
  <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={6}>
    {items.map((item) => (
      <Box
        key={item.label}
        bg="white"
        borderWidth="1px"
        borderColor={orderBorder}
        borderRadius="22px"
        px={4}
        py={4}
      >
        <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.08em">
          {item.label}
        </Text>
        <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" color="gray.900" mt={2}>
          {item.value}
        </Text>
        {item.hint ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {item.hint}
          </Text>
        ) : null}
      </Box>
    ))}
  </SimpleGrid>
);

export const OrderStatusBadge = ({ status }: { status?: string }) => {
  const meta = getOrderStatusMeta(status);
  return (
    <Badge colorScheme={meta.colorScheme} px={2.5} py={1} borderRadius="999px" fontWeight="700">
      {meta.label}
    </Badge>
  );
};

export const OrderPaymentBadge = ({ status }: { status?: string }) => {
  const meta = getOrderPaymentMeta(status);
  return (
    <Badge colorScheme={meta.colorScheme} px={2.5} py={1} borderRadius="999px" fontWeight="700">
      {meta.label}
    </Badge>
  );
};

export const OrderFulfillmentBadge = ({ status }: { status?: string }) => {
  const meta = getOrderFulfillmentMeta(status);
  return (
    <Badge colorScheme={meta.colorScheme} px={2.5} py={1} borderRadius="999px" fontWeight="700">
      {meta.label}
    </Badge>
  );
};

export const OrderField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" color="gray.500" mb={1}>
      {label}
    </Text>
    <Box color="gray.900" fontWeight="600">
      {value}
    </Box>
  </Box>
);

export const OrderCommandBar = ({
  search,
  onSearchChange,
  inputRef,
  status,
  onStatusChange,
  source,
  onSourceChange,
  sort,
  onSortChange,
  view,
  onViewChange,
  resultLabel,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  status: string;
  onStatusChange: (value: string) => void;
  source: string;
  onSourceChange: (value: string) => void;
  sort: string;
  onSortChange: (value: string) => void;
  view: 'table' | 'cards';
  onViewChange: (value: 'table' | 'cards') => void;
  resultLabel: string;
}) => (
  <Stack
    spacing={4}
    bg="rgba(255,255,255,0.9)"
    borderWidth="1px"
    borderColor={orderBorder}
    borderRadius="24px"
    p={{ base: 4, md: 5 }}
    mb={5}
    position="sticky"
    top={{ base: '72px', md: '84px' }}
    zIndex={5}
    backdropFilter="blur(14px)"
  >
    <Flex
      direction={{ base: 'column', xl: 'row' }}
      justify="space-between"
      align={{ base: 'stretch', xl: 'center' }}
      gap={3}
    >
      <InputGroup maxW={{ xl: '420px' }}>
        <InputLeftElement pointerEvents="none">
          <Search size={16} color="#718096" />
        </InputLeftElement>
        <Input
          ref={inputRef}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Search by order, customer, company, or amount"
          borderRadius="16px"
          bg="white"
        />
      </InputGroup>

      <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
        <Select
          value={status}
          onChange={(event) => onStatusChange(event.target.value)}
          borderRadius="16px"
          bg="white"
          minW={{ md: '170px' }}
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="processing">Processing</option>
          <option value="shipped">Shipped</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </Select>
        <Select
          value={source}
          onChange={(event) => onSourceChange(event.target.value)}
          borderRadius="16px"
          bg="white"
          minW={{ md: '170px' }}
        >
          <option value="all">All sources</option>
          <option value="wa">WhatsApp</option>
          <option value="fb">Facebook</option>
          <option value="insta">Instagram</option>
          <option value="web">Website</option>
          <option value="phone">Phone</option>
          <option value="other">Other</option>
        </Select>
        <Select
          value={sort}
          onChange={(event) => onSortChange(event.target.value)}
          borderRadius="16px"
          bg="white"
          minW={{ md: '190px' }}
        >
          <option value="created-desc">Newest first</option>
          <option value="created-asc">Oldest first</option>
          <option value="amount-desc">Highest amount</option>
          <option value="amount-asc">Lowest amount</option>
        </Select>
      </Stack>
    </Flex>

    <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} direction={{ base: 'column', md: 'row' }}>
      <Text fontSize="sm" color="gray.500">
        {resultLabel}
      </Text>
      <HStack spacing={2}>
        <IconButton
          aria-label="Table view"
          icon={<Rows3 size={16} />}
          borderRadius="14px"
          variant={view === 'table' ? 'solid' : 'outline'}
          bg={view === 'table' ? orderAccent : undefined}
          color={view === 'table' ? 'white' : undefined}
          _hover={view === 'table' ? { bg: orderAccentDark } : undefined}
          onClick={() => onViewChange('table')}
        />
        <IconButton
          aria-label="Card view"
          icon={<LayoutGrid size={16} />}
          borderRadius="14px"
          variant={view === 'cards' ? 'solid' : 'outline'}
          bg={view === 'cards' ? orderAccent : undefined}
          color={view === 'cards' ? 'white' : undefined}
          _hover={view === 'cards' ? { bg: orderAccentDark } : undefined}
          onClick={() => onViewChange('cards')}
        />
      </HStack>
    </Flex>
  </Stack>
);

export const OrderEmptyState = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <VStack
    py={{ base: 14, md: 20 }}
    px={6}
    spacing={3}
    bg="white"
    borderRadius="24px"
    borderWidth="1px"
    borderColor={orderBorder}
    textAlign="center"
  >
    <Text fontWeight="800" fontSize="lg" color="gray.900">
      {title}
    </Text>
    <Text color="gray.500" maxW="md">
      {description}
    </Text>
    {action}
  </VStack>
);

export const OrderDashboardSkeleton = () => (
  <Stack spacing={6}>
    <Skeleton h="180px" borderRadius="28px" />
    <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton key={index} h="110px" borderRadius="22px" />
      ))}
    </SimpleGrid>
    <Skeleton h="110px" borderRadius="24px" />
    <Stack spacing={4}>
      {Array.from({ length: 5 }).map((_, index) => (
        <Box key={index} bg="white" borderWidth="1px" borderColor={orderBorder} borderRadius="22px" p={5}>
          <SkeletonText noOfLines={3} spacing="3" skeletonHeight="4" />
        </Box>
      ))}
    </Stack>
  </Stack>
);
